import { FC, useEffect, useState } from 'react';
import { Alert, Checkbox, FormControlLabel } from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgCustomer } from 'services/api/paymentGateway/types';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { useIdempotencyKey } from '../common/helpers';

type TChargeCustomerDialogProps = {
	open: boolean;
	customer: TPgCustomer;
	onClose: () => void;
};

/**
 * "Charge all open invoices" (`charge-customer`, 03 §4.8): a Hangfire job charges every unpaid invoice
 * of the customer like the monthly cycle does, but without the schedule days. The key is created when
 * the dialog opens and the button is locked after the gateway accepted the job, so a double click
 * cannot start the charge twice.
 */
export const ChargeCustomerDialog: FC<TChargeCustomerDialogProps> = ({
	open,
	customer,
	onClose,
}) => {
	const [idempotencyKey] = useIdempotencyKey(open);
	const [ignoreRule, setIgnoreRule] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [jobId, setJobId] = useState<string | null>(null);

	useEffect(() => {
		if (!open) return;
		setIgnoreRule(false);
		setBusy(false);
		setError(null);
		setJobId(null);
	}, [open]);

	const submit = async (reason: string) => {
		setBusy(true);
		setError(null);
		try {
			const response = await api.paymentGateway.chargeCustomer({
				customerId: customer.id,
				reason,
				idempotencyKey,
				ignorePayabilityRule: ignoreRule || undefined,
			});
			setJobId(response.jobId);
		} catch (e) {
			setError(getPgErrorMessage(e, 'Failed to start the charge'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<PgConfirmDialog
			open={open}
			title="Charge all open invoices"
			confirmLabel="Charge"
			confirmColor="error"
			reasonLabel="Reason (shown in the audit log)"
			confirmDisabled={!!jobId}
			busy={busy}
			error={error}
			onConfirm={(reason) => void submit(reason)}
			onClose={onClose}
			description={`This charges every unpaid invoice of ${customer.clientNumber}${
				customer.displayName ? ` (${customer.displayName})` : ''
			} with the saved payment methods now, in the order of the services. Money is taken from the customer. The charge runs as a background job: watch the Payments tab for the results.`}
		>
			<FormControlLabel
				control={
					<Checkbox
						checked={ignoreRule}
						onChange={(e) => setIgnoreRule(e.target.checked)}
						disabled={busy || !!jobId}
					/>
				}
				label="Ignore the debt transfer rule"
			/>
			{jobId && (
				<Alert severity="success" sx={{ mt: 1 }}>
					The charge job is started (job {jobId}).
				</Alert>
			)}
		</PgConfirmDialog>
	);
};
